/**
 * GuardianDog — the sleeping German Shepherd in the Liminal Atrium.
 *
 * Canonical role (2026-04-14 companion book, p.7): the dog is the single
 * most important illustration of the "Concurrent Puzzle" thesis. The path
 * to the crystal chamber passes across the dog's rug. Walking directly at
 * it with footsteps/noise wakes it — it stands, growls, and blocks the
 * path. To continue the player must either:
 *
 *   (A) have Minna activate Calming Resonance (The Purr) on approach, OR
 *   (B) have Basil wind the rusted `Gramophone`, whose sleepy melody lulls
 *       the dog back to sleep.
 *
 * Before this class landed, the same beat was an invisible rug-trigger
 * that teleported Minna back to the spawn checkpoint — the canon's
 * flagship concurrent-puzzle teaching moment was hidden behind a respawn.
 * The rug trigger has been removed from `LiminalAtrium.ts`; this NPC
 * owns the full state machine, listens to the gramophone/purr events,
 * and renders its sight + sound cones per the Option E hybrid treatment
 * spec'd for foxes (`docs/specs/SPEC-fox-sight-cone.md`).
 *
 * States:
 *   - **sleeping** (default) — dog lies curled on the rug, breathing idle,
 *     cones very faint.
 *   - **stirring** — either cat is inside the sound cone. Dog raises its
 *     head, cones pulse amber. 1.2s grace window in which the player can
 *     back out or trigger the gramophone/purr to avoid the full alert.
 *   - **alert** — dog is standing, growling, path blocked. Cones go red
 *     and widen. Stays until the gramophone lullaby plays, the purr stays
 *     active for 1 second, or the dog times out (6 s) with both cats
 *     outside its sight cone.
 *   - **resettling** — dog sinks back down, cones fade. 1.5 s before
 *     returning to sleeping.
 *
 * Cones are visualised with the Option E hybrid treatment:
 *   - Always-on dim ground disc-slice showing the sound-cone footprint
 *     (so players learn the zone even on their first approach).
 *   - A volumetric wedge that appears when the dog is stirring or alert.
 *
 * This class extends the base NPC kinematic capsule; the capsule stays
 * invisible while the authored `guardian_dog_sleeping.glb` is the visible
 * mesh. Movement is not physics-driven — the dog only rotates its head
 * and scales its body for the "rise from sleep" beat.
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { PointLight } from "@babylonjs/core/Lights/pointLight";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { NPC, NPCConfig } from "./NPC";
import { gameEvents } from "../../core/EventBus";
import { GameEvents } from "../../config/events";
import { COLORS } from "../../config/GameConfig";

/**
 * Distinct state machine for the GuardianDog — intentionally separate from
 * the base `NPCBehavior` state machine (which is keyed to Minna's purr).
 * The dog's cycle is driven by proximity + lullaby events, not calm meter.
 */
export type GuardianDogState = "sleeping" | "stirring" | "alert" | "resettling";

export interface GuardianDogConfig {
  /** Unique key (used in events). */
  key: string;
  /** World-space spawn position. The rug is centered here. */
  position: Vector3;
  /** Facing direction (radians, Y axis). 0 = faces +X. */
  facingY?: number;
  /** Half-radius of the dog's awareness (world units). Default 3.0. */
  soundRadius?: number;
  /** Half-angle of the dog's sight cone in radians. Default ~55deg. */
  sightHalfAngle?: number;
  /** Range of the sight cone (world units). Default 4.0. */
  sightRange?: number;
  /** How many seconds in `stirring` before the dog escalates to `alert`. */
  stirringGraceSeconds?: number;
  /** How many seconds the purr must hold to resettle a fully-alert dog. */
  purrResettleSeconds?: number;
  /** How long the dog stays alert after losing both cats from its sight. */
  alertTimeoutSeconds?: number;
  /** How long the `resettling` animation plays before sleeping. */
  resettleDurationSeconds?: number;
}

const DEFAULTS: Required<Omit<GuardianDogConfig, "key" | "position">> = {
  facingY: 0,
  soundRadius: 3.0,
  sightHalfAngle: Math.PI * 0.32, // ~58° half-angle
  sightRange: 4.0,
  stirringGraceSeconds: 1.2,
  purrResettleSeconds: 1.0,
  alertTimeoutSeconds: 6.0,
  resettleDurationSeconds: 1.5,
};

/** Player probe exposed for `tick()`. */
export interface GuardianDogPlayerProbe {
  /** World position. */
  position: Vector3;
  /** True if Minna is actively purring near the dog (CalmingResonance in range). */
  purring: boolean;
  /** Shadow-slip stealth state — stealthed cats don't wake the dog. */
  stealthed: boolean;
}

/**
 * GuardianDog extends the base NPC for capsule + model loading, but carries
 * its own state machine (purr/lullaby-driven rather than calm-meter-driven).
 */
export class GuardianDog extends NPC {
  public dogState: GuardianDogState = "sleeping";

  private readonly cfg: Required<GuardianDogConfig>;
  /** Seconds the dog has been in the current state. */
  private stateElapsed = 0;
  /** Continuous purr-in-range seconds (resettle counter while alert). */
  private purrInRangeElapsed = 0;
  /** Continuous player-absent seconds (timeout counter while alert). */
  private noPlayerElapsed = 0;
  /** True once the block-path event has been emitted for the current alert cycle. */
  private blockedEmitted = false;

  private groundCone: Mesh | null = null;
  private sightWedge: Mesh | null = null;
  private growlLight: PointLight;
  private sightMat: StandardMaterial;

  private modelRoot: Mesh | null = null;

  constructor(scene: Scene, config: GuardianDogConfig) {
    const cfg: Required<GuardianDogConfig> = {
      ...DEFAULTS,
      facingY: config.facingY ?? DEFAULTS.facingY,
      soundRadius: config.soundRadius ?? DEFAULTS.soundRadius,
      sightHalfAngle: config.sightHalfAngle ?? DEFAULTS.sightHalfAngle,
      sightRange: config.sightRange ?? DEFAULTS.sightRange,
      stirringGraceSeconds: config.stirringGraceSeconds ?? DEFAULTS.stirringGraceSeconds,
      purrResettleSeconds: config.purrResettleSeconds ?? DEFAULTS.purrResettleSeconds,
      alertTimeoutSeconds: config.alertTimeoutSeconds ?? DEFAULTS.alertTimeoutSeconds,
      resettleDurationSeconds: config.resettleDurationSeconds ?? DEFAULTS.resettleDurationSeconds,
      key: config.key,
      position: config.position.clone(),
    };

    const npcConfig: NPCConfig = {
      key: config.key,
      position: config.position,
      // Capsule sized to match the sleeping-dog silhouette; invisible
      // anyway since the model loads as the visible mesh.
      height: 0.8,
      radius: 0.5,
      color: "#6a4a2a",
      behavior: {
        initialState: "idle",
        calmRadius: cfg.soundRadius,
        // The dog ignores the generic calm-meter path — its purr resolution
        // runs through `tick()` below — but we wire a plausible radius so
        // the base class's calm-bar UI never surfaces accidentally.
        calmThreshold: 100,
        calmRate: 0,
        calmDecay: 0,
      },
      modelPath: "/assets/models/npcs/guardian_dog_sleeping.glb",
      modelScale: 1.0,
      modelOffset: new Vector3(0, -0.4, 0),
      modelRotationY: cfg.facingY,
    };

    super(scene, npcConfig);
    this.cfg = cfg;
    this.mesh.rotation.y = cfg.facingY;

    // Red growl light — off while sleeping, pulses while alert.
    this.growlLight = new PointLight(`guardianDog_${config.key}_growl`, Vector3.Zero(), scene);
    this.growlLight.diffuse = new Color3(1.0, 0.18, 0.05);
    this.growlLight.specular = Color3.Black();
    this.growlLight.intensity = 0;
    this.growlLight.range = 4;
    this.growlLight.parent = this.mesh;
    this.growlLight.position = new Vector3(0.6, 0.4, 0);

    // Option E hybrid cone visualisation — always-on ground disc plus a
    // volumetric wedge shown during stirring/alert. Shared material so
    // only one pipeline cost per frame.
    this.sightMat = new StandardMaterial(`guardianDog_${config.key}_coneMat`, scene);
    this.sightMat.disableLighting = true;
    this.sightMat.diffuseColor = Color3.FromHexString(COLORS.BRASS);
    this.sightMat.emissiveColor = Color3.FromHexString(COLORS.BRASS);
    this.sightMat.specularColor = Color3.Black();
    this.sightMat.alpha = 0.18;

    // Disc-slice on the ground showing the sound footprint.
    this.groundCone = MeshBuilder.CreateDisc(
      `guardianDog_${config.key}_groundCone`,
      { radius: cfg.soundRadius, tessellation: 24, arc: 1.0 },
      scene,
    );
    this.groundCone.parent = this.mesh;
    this.groundCone.rotation.x = Math.PI / 2;
    this.groundCone.position.y = -0.38;
    this.groundCone.material = this.sightMat;
    this.groundCone.isPickable = false;

    // Raised wedge showing the forward sight cone. Only shown while
    // stirring/alert so the sleeping dog doesn't look perpetually on-guard.
    const wedgeRadius = cfg.sightRange;
    const wedgeArc = (cfg.sightHalfAngle * 2) / (Math.PI * 2);
    this.sightWedge = MeshBuilder.CreateDisc(
      `guardianDog_${config.key}_sightWedge`,
      { radius: wedgeRadius, tessellation: 24, arc: wedgeArc },
      scene,
    );
    this.sightWedge.parent = this.mesh;
    this.sightWedge.rotation.x = Math.PI / 2;
    // Orient the wedge forward (dog faces along facingY; mesh already
    // carries that rotation, so the wedge stays in local space with no
    // extra transform needed beyond the arc offset below).
    this.sightWedge.rotation.z = -cfg.sightHalfAngle; // center the arc on +X
    this.sightWedge.position.y = -0.35;
    this.sightWedge.material = this.sightMat;
    this.sightWedge.isVisible = false;
    this.sightWedge.isPickable = false;

    // The procedural capsule is invisible once the GLB loads; hide the
    // calm-bar plane preemptively via an NPC protected helper by staying
    // out of the calming/frantic states (tick() only uses 'idle').
    this.mesh.isVisible = false;
  }

  /**
   * Register an external subscription that resettles the dog when the
   * gramophone plays. The Gramophone.play() call emits
   * GRAMOPHONE_PLAYED; we listen and collapse the dog back to sleep
   * regardless of what state it's currently in. Called from
   * `LiminalAtrium.ts` after both NPCs are instantiated so the event
   * wiring lives at the scene layer, not inside the NPC constructor.
   */
  listenForGramophone(): () => void {
    return gameEvents.on(GameEvents.GRAMOPHONE_PLAYED, (payload) => {
      const { key: _gramoKey } = payload as { key: string };
      // Any gramophone in the scene resettles the dog — there's only one
      // in L0, but we don't hard-gate on the key so a future level could
      // share the mechanic without rewiring.
      this.resettle("gramophone");
    });
  }

  /**
   * Per-frame tick called from the scene's game loop. Takes a list of
   * player probes (usually [Minna, Basil]) and drives the state machine
   * based on proximity + purr state. This is a standalone loop rather
   * than piggybacking on the base NPC calm-meter path because the canon
   * beat requires distinct timings (grace window, alert timeout, purr
   * resettle window).
   */
  tick(deltaSeconds: number, players: GuardianDogPlayerProbe[]): void {
    this.stateElapsed += deltaSeconds;

    // Resolve the "awakening" signal: at least one non-stealthed player is
    // inside the sound radius OR inside the sight cone.
    let playerDetected = false;
    let purringInRange = false;

    for (const probe of players) {
      const dx = probe.position.x - this.mesh.position.x;
      const dy = probe.position.y - this.mesh.position.y;
      const distSq = dx * dx + dy * dy;

      if (probe.purring) {
        if (distSq <= this.cfg.soundRadius * this.cfg.soundRadius * 1.15) {
          purringInRange = true;
        }
      }

      if (probe.stealthed) continue;

      if (distSq <= this.cfg.soundRadius * this.cfg.soundRadius) {
        playerDetected = true;
      } else {
        // Check sight cone — forward vector is +X rotated by facingY.
        const angle = Math.atan2(dy, dx) - this.cfg.facingY;
        const normalizedAngle = Math.atan2(Math.sin(angle), Math.cos(angle));
        if (
          Math.abs(normalizedAngle) <= this.cfg.sightHalfAngle &&
          distSq <= this.cfg.sightRange * this.cfg.sightRange
        ) {
          playerDetected = true;
        }
      }
    }

    if (purringInRange) {
      this.purrInRangeElapsed += deltaSeconds;
    } else {
      this.purrInRangeElapsed = 0;
    }

    if (!playerDetected) {
      this.noPlayerElapsed += deltaSeconds;
    } else {
      this.noPlayerElapsed = 0;
    }

    // Drive state transitions.
    switch (this.dogState) {
      case "sleeping":
        if (playerDetected) {
          this.transitionTo("stirring", {
            source: this.closestPlayerKey(players),
            triggerPos: this.closestPlayerPos(players),
          });
        } else if (purringInRange) {
          // Purring near an already-sleeping dog just keeps it asleep.
        }
        break;
      case "stirring":
        if (purringInRange) {
          // Purr during the grace window: drop straight back to sleeping.
          this.resettle("purr");
        } else if (this.stateElapsed >= this.cfg.stirringGraceSeconds) {
          this.transitionTo("alert", {});
        } else if (!playerDetected) {
          // Players backed out during the grace window — return to sleep.
          this.resettle("timeout");
        }
        break;
      case "alert":
        if (this.purrInRangeElapsed >= this.cfg.purrResettleSeconds) {
          this.resettle("purr");
        } else if (this.noPlayerElapsed >= this.cfg.alertTimeoutSeconds) {
          this.resettle("timeout");
        } else if (!this.blockedEmitted) {
          this.blockedEmitted = true;
          gameEvents.emit(GameEvents.GUARDIAN_DOG_BLOCKED_PATH, {
            npcKey: this.npcKey,
            message:
              "The guardian is awake. Play the gramophone or purr to soothe them back to sleep.",
          });
        }
        break;
      case "resettling":
        if (this.stateElapsed >= this.cfg.resettleDurationSeconds) {
          this.transitionTo("sleeping", {});
        }
        break;
    }

    this.updateVisuals();
  }

  /**
   * Public escape hatch — forcibly resettle the dog. Called from the
   * gramophone event listener and also exposed for tests.
   */
  resettle(reason: "gramophone" | "purr" | "timeout"): void {
    if (this.dogState === "sleeping" || this.dogState === "resettling") return;
    this.transitionTo("resettling", {});
    gameEvents.emit(GameEvents.GUARDIAN_DOG_SETTLED, {
      npcKey: this.npcKey,
      reason,
    });
  }

  /** Public for tests — forward distance + bearing into the sight cone. */
  isPointInSightCone(worldX: number, worldY: number): boolean {
    const dx = worldX - this.mesh.position.x;
    const dy = worldY - this.mesh.position.y;
    const distSq = dx * dx + dy * dy;
    if (distSq > this.cfg.sightRange * this.cfg.sightRange) return false;
    const angle = Math.atan2(dy, dx) - this.cfg.facingY;
    const normalized = Math.atan2(Math.sin(angle), Math.cos(angle));
    return Math.abs(normalized) <= this.cfg.sightHalfAngle;
  }

  /** Public for tests — distance check against the sound radius. */
  isPointInSoundRadius(worldX: number, worldY: number): boolean {
    const dx = worldX - this.mesh.position.x;
    const dy = worldY - this.mesh.position.y;
    return dx * dx + dy * dy <= this.cfg.soundRadius * this.cfg.soundRadius;
  }

  override dispose(): void {
    this.growlLight.dispose();
    this.groundCone?.dispose();
    this.sightWedge?.dispose();
    this.sightMat.dispose();
    this.modelRoot?.dispose();
    super.dispose();
  }

  // ─── Internal ────────────────────────────────────────────────────

  private transitionTo(
    next: GuardianDogState,
    context: {
      source?: string;
      triggerPos?: { x: number; y: number };
    },
  ): void {
    if (this.dogState === next) return;
    this.dogState = next;
    this.stateElapsed = 0;

    if (next === "stirring") {
      this.blockedEmitted = false;
      gameEvents.emit(GameEvents.GUARDIAN_DOG_STIRRED, {
        npcKey: this.npcKey,
        source: context.source ?? "unknown",
        at: context.triggerPos ?? {
          x: this.mesh.position.x,
          y: this.mesh.position.y,
        },
      });
    }

    if (next === "alert") {
      this.blockedEmitted = false;
    }

    if (next === "sleeping") {
      this.purrInRangeElapsed = 0;
      this.noPlayerElapsed = 0;
      this.blockedEmitted = false;
    }

    this.updateVisuals();
  }

  private updateVisuals(): void {
    const timeNow = performance.now() / 1000;
    let coneAlpha = 0.12;
    let coneColor = Color3.FromHexString(COLORS.BRASS);
    let growl = 0;
    let wedgeVisible = false;

    switch (this.dogState) {
      case "sleeping":
        coneAlpha = 0.08;
        coneColor = Color3.FromHexString(COLORS.BRASS);
        wedgeVisible = false;
        growl = 0;
        break;
      case "stirring":
        coneAlpha = 0.22 + Math.sin(timeNow * 4) * 0.08;
        coneColor = Color3.FromHexString("#d68a2c"); // amber
        wedgeVisible = true;
        growl = 0.6;
        break;
      case "alert":
        coneAlpha = 0.38 + Math.sin(timeNow * 8) * 0.1;
        coneColor = Color3.FromHexString("#b04820"); // copper-red
        wedgeVisible = true;
        growl = 1.8 + Math.sin(timeNow * 12) * 0.4;
        break;
      case "resettling":
        coneAlpha = 0.16;
        coneColor = Color3.FromHexString(COLORS.BRASS);
        wedgeVisible = true;
        growl = 0;
        break;
    }

    this.sightMat.alpha = coneAlpha;
    this.sightMat.diffuseColor = coneColor;
    this.sightMat.emissiveColor = coneColor;
    if (this.sightWedge) this.sightWedge.isVisible = wedgeVisible;
    this.growlLight.intensity = growl;

    // Breathing idle — gentle Y scale pulse on the imported model, so the
    // sleeping dog visibly rises and falls. Skipped while alert (rigid
    // posture) and while resettling (the pose animates via Y-scale ramp).
    if (this.visualModel) {
      for (const root of this.visualModel.rootNodes) {
        if (!(root as { scaling?: Vector3 }).scaling) continue;
        const s = (root as unknown as { scaling: Vector3 }).scaling;
        if (this.dogState === "sleeping") {
          const breath = 1 + Math.sin(timeNow * 1.2) * 0.02;
          s.y = breath;
        } else if (this.dogState === "stirring") {
          // Head-lift beat: gentle uniform puff.
          s.y = 1.05 + Math.sin(timeNow * 3) * 0.02;
        } else if (this.dogState === "alert") {
          s.y = 1.12;
        } else if (this.dogState === "resettling") {
          const pct = Math.min(1, this.stateElapsed / this.cfg.resettleDurationSeconds);
          s.y = 1.12 - pct * 0.12;
        }
      }
    }
  }

  private closestPlayerKey(players: GuardianDogPlayerProbe[]): string {
    // Heuristic only — the probe array doesn't carry keys, so we return a
    // generic "player" label for the `source` field. Scene code can
    // override this by passing richer probes if needed.
    return players.length > 0 ? "player" : "unknown";
  }

  private closestPlayerPos(players: GuardianDogPlayerProbe[]): { x: number; y: number } {
    let bestX = this.mesh.position.x;
    let bestY = this.mesh.position.y;
    let bestDistSq = Infinity;
    for (const probe of players) {
      const dx = probe.position.x - this.mesh.position.x;
      const dy = probe.position.y - this.mesh.position.y;
      const d = dx * dx + dy * dy;
      if (d < bestDistSq) {
        bestDistSq = d;
        bestX = probe.position.x;
        bestY = probe.position.y;
      }
    }
    return { x: bestX, y: bestY };
  }
}
